/**
 * 課題管理の項目の値を、人が読める1行にする（変更履歴と競合の知らせで使う）。
 *
 * 選択肢の値（判定・優先度など）は文言に、メールは表示名に、真偽は「はい・いいえ」（開発するかは「要・不要」）にし、
 * 長い文は切る。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の項目の内部の値（判定・優先度・メール・真偽・配列）を、画面で読める1行の文言にする
 * @graph-connects ISSUE_VALUE_LABEL_KEYS [references] 選択肢の値の文言
 */
import { ISSUE_VALUE_LABEL_KEYS } from './issue-mgmt-format.js';

/**
 * 文言の関数（react-i18next の t）。
 * @graph-connects none
 */
export type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * 値の表示に使う最大の字数（長い文は切って「…」を付ける）。
 * @graph-connects none
 */
const VALUE_PREVIEW_LENGTH = 30;

/**
 * 項目の値を1行にする。
 * @param field 項目名（選択肢の文言を引くため。分からなければ空）
 * @param nameOf メールから表示名を引く
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の項目の値を、選択肢は文言・メールは名前・真偽ははい/いいえにして、長い文は切って1行にする
 * @graph-connects ISSUE_VALUE_LABEL_KEYS [references] 選択肢の値の文言
 */
export function formatIssueFieldValue(
  value: unknown,
  field: string,
  t: Translate,
  nameOf: (email: string) => string,
): string {
  if (value === null || value === undefined || value === '') return t('issueMgmt.error.emptyValue');
  if (typeof value === 'boolean') {
    if (field === 'devRequired') {
      return value ? t('issueMgmt.detail.devYes') : t('issueMgmt.detail.devNo');
    }
    return value ? t('issueMgmt.common.yes') : t('issueMgmt.common.no');
  }
  if (Array.isArray(value)) {
    return value.length === 0
      ? t('issueMgmt.error.emptyValue')
      : value.map((v) => formatIssueFieldValue(v, field, t, nameOf)).join('、');
  }
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  const labelKey = ISSUE_VALUE_LABEL_KEYS[field]?.[text];
  if (labelKey !== undefined) return t(labelKey);
  if (text.includes('@')) return nameOf(text);
  return text.length > VALUE_PREVIEW_LENGTH ? `${text.slice(0, VALUE_PREVIEW_LENGTH)}…` : text;
}
